import { type Entity } from '../entities/Entity';
import { radiansToDegrees } from '../math/MathUtils';
import { type Camera } from '../rendering/Camera';
import { type Scene } from '../scene/Scene';
import { formatDebugValue } from './DebugValue';
import {
  type CameraSnapshot,
  type ComponentSnapshot,
  type EntitySnapshot,
  type SceneSnapshot,
  type TransformSnapshot,
} from './SceneSnapshot';

/**
 * Turns a live scene into a {@link SceneSnapshot}.
 *
 * The whole read-only introspection API is these two functions. There is no registry of inspectable
 * types and no per-component code to keep in step, because a component is plain data: its own
 * enumerable fields are its whole state, and rendering one is `formatDebugValue`'s problem.
 *
 * Behaviors are named but not opened. Their state is private by design, and a behavior that wants to
 * be visible puts what matters on a component, which is where entity state belongs anyway.
 */
export function inspectScene(scene: Scene): SceneSnapshot {
  return {
    name: scene.name,
    entityCount: scene.entityCount,
    camera: inspectCamera(scene.camera),
    roots: scene.entities.filter((entity) => entity.parent === undefined).map(inspectEntity),
  };
}

/** Snapshots one entity and its whole subtree. */
export function inspectEntity(entity: Entity): EntitySnapshot {
  return {
    id: entity.id,
    name: entity.name,
    active: entity.active,
    activeInHierarchy: entity.activeInHierarchy,
    tags: [...entity.tags],
    transform: inspectTransform(entity),
    components: entity.components.map(inspectComponent),
    behaviors: entity.behaviors.map((behavior) => behavior.constructor.name),
    children: entity.children.map(inspectEntity),
  };
}

function inspectCamera(camera: Camera): CameraSnapshot {
  const { worldWidth, worldHeight, aspectRatio } = camera.viewport;
  return { x: camera.position.x, y: camera.position.y, worldWidth, worldHeight, aspectRatio };
}

function inspectTransform(entity: Entity): TransformSnapshot {
  const { position, rotation, scale } = entity.transform;
  return {
    x: position.x,
    y: position.y,
    rotationDegrees: radiansToDegrees(rotation),
    scaleX: scale.x,
    scaleY: scale.y,
  };
}

function inspectComponent(component: object): ComponentSnapshot {
  const fields = Object.entries(component as Record<string, unknown>).map(([name, value]) => ({
    name,
    value: formatDebugValue(value),
  }));
  return { type: component.constructor.name, fields };
}
